import classnames from 'classnames';
import React, { FC, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';

import { useTrackErrorStateAnalytics } from '../../../utils/hooks/bi/useTrackErrorStateAnalytics';
import { useLoadingErrorCatcher } from '../../../utils/hooks/useLoadingErrorCatcher';
import { getErrorMessage } from '../../../utils/hooks/useLoadingErrorCatcher/useLoadingErrorCatcher.utils';
import { MediaManagerSDKAnyItemWithSectionInfo, MediaManagerSDKFolder } from '../../../utils/media-manager-sdk';
import { isMediaFromWix } from '../../../utils/media-manager-sdk/sources';
import { getEmptyStatesType } from './EmptyState.utils';

import { EmptyStatesConfigs } from './EmptyStatesConfigs';
import { EmptyStateType, IOuter } from './EmptyState.types';

import { EmptyState as WSREmptyState } from '../../../components/common';
import { MediaFromWix } from './MediaFromWix/MediaFromWix';

import styles from './EmptyState.scss';

export const EmptyState: FC<IOuter> = (props) => {
  const {
    source,
    searchQuery,
    extendedFiltersElement,
    className,
    dataHook = 'empty-state',
    currentFolder,
    settingsLayout,
    isUnknownError,
    isOffline,
    isEnterprise,
    items,
    onItemClick,
    selectedItems,
    isItemSelected,
    buyItem,
    onItemRightClick,
    isSecure,
    publicMediaCurrentCategory,
    publicMediaRootCategories,
    openExpandedView,
  } = props;

  const { loadingError, isNotAuthorized, isNetworkError } = useLoadingErrorCatcher();
  const { trackErrorScreenRendered } = useTrackErrorStateAnalytics();
  const location = useLocation();

  const type = useMemo(
    () =>
      getEmptyStatesType({
        source,
        isNotAuthorized,
        isUnknownError,
        isNetworkError,
        isOffline,
        isEnterprise,
        location,
        currentFolder,
        searchQuery,
      }),
    [source, isNotAuthorized, isUnknownError, currentFolder, searchQuery, location, isNetworkError, isEnterprise],
  );

  const config = EmptyStatesConfigs[type];

  useEffect(() => {
    if (loadingError) {
      trackErrorScreenRendered(source, settingsLayout, getErrorMessage(loadingError));
    }
  }, []);

  const shouldRenderSubtitle = Boolean(config.Subtitle);
  const header = extendedFiltersElement && <div className={styles.filtersWrapper}>{extendedFiltersElement}</div>;

  return isMediaFromWix(source) && !!items?.length ? (
    <MediaFromWix
      items={items as MediaManagerSDKAnyItemWithSectionInfo[]}
      header={header}
      searchQuery={searchQuery}
      onItemClick={onItemClick}
      selectedItems={selectedItems}
      isItemSelected={isItemSelected}
      buyItem={buyItem}
      onItemRightClick={onItemRightClick}
      isSecure={isSecure}
      publicMediaCurrentCategory={publicMediaCurrentCategory}
      openExpandedView={openExpandedView}
      currentFolder={currentFolder as MediaManagerSDKFolder}
      publicMediaRootCategories={publicMediaRootCategories}
    />
  ) : (
    <div
      className={classnames(
        styles.wrapper,
        {
          [styles.siteFilesEmptyState]: type === EmptyStateType.SiteFilesNewDnD,
          [styles.enterpriseEmptyState]: type === EmptyStateType.Enterprise,
        },
        className,
      )}
      data-hook={dataHook}
    >
      {header}

      <div className={styles.contentWrapper}>
        <WSREmptyState
          title={config.Title && <config.Title {...props} />}
          subtitle={shouldRenderSubtitle && <config.Subtitle {...props} />}
          image={config.image}
          skin="page"
          dataHook="wsr-empty-state"
          className={styles.emptyState}
        >
          {config.Button && <config.Button {...props} {...config.buttonProps} />}
        </WSREmptyState>
      </div>
    </div>
  );
};
